import type { ChangeEvent } from 'react';

// ============================================================================
// FieldEditors
// ----------------------------------------------------------------------------
// Small wrappers over native inputs with consistent styling. Each editor
// follows the same shape: takes value + onChange, renders a labelless input
// (the label lives in FieldRow). All variants accept null and treat empty
// strings as null on output, so the database gets clean nulls for blank
// optional fields.
// ============================================================================

const inputBase =
    'w-full px-2.5 py-1.5 text-sm bg-pt-dark border rounded-md text-pt-text ' +
    'placeholder:text-pt-text-dim focus:outline-none transition-colors';
const inputDefault = 'border-pt-input-border focus:border-pt-gold';
const inputError = 'border-pt-red focus:border-pt-red';

// ----------------------------------------------------------------------------
// TextInput
// ----------------------------------------------------------------------------

export function TextInput({
    value,
    onChange,
    placeholder,
    error,
    maxLength
}: {
    value: string | null;
    onChange: (v: string | null) => void;
    placeholder?: string;
    error?: boolean;
    maxLength?: number;
}) {
    return (
        <input
            type="text"
            value={value ?? ''}
            onChange={(e: ChangeEvent<HTMLInputElement>) =>
                onChange(e.target.value === '' ? null : e.target.value)
            }
            placeholder={placeholder}
            maxLength={maxLength}
            className={`${inputBase} ${error ? inputError : inputDefault}`}
        />
    );
}

// ----------------------------------------------------------------------------
// TextareaInput — for description, notes, out-of-service reason
// ----------------------------------------------------------------------------

export function TextareaInput({
    value,
    onChange,
    placeholder,
    rows = 3,
    error,
    required
}: {
    value: string | null;
    onChange: (v: string | null) => void;
    placeholder?: string;
    rows?: number;
    error?: boolean;
    required?: boolean;
}) {
    return (
        <textarea
            value={value ?? ''}
            onChange={(e) =>
                onChange(e.target.value === '' ? null : e.target.value)
            }
            placeholder={placeholder}
            rows={rows}
            required={required}
            className={`${inputBase} ${error ? inputError : inputDefault} resize-y`}
        />
    );
}

// ----------------------------------------------------------------------------
// NumberInput — for prices, quantities, intervals
// ----------------------------------------------------------------------------

export function NumberInput({
    value,
    onChange,
    placeholder,
    min,
    max,
    step = 1,
    error,
    suffix
}: {
    value: number | null;
    onChange: (v: number | null) => void;
    placeholder?: string;
    min?: number;
    max?: number;
    step?: number | string;
    error?: boolean;
    suffix?: string;
}) {
    const inner = (
        <input
            type="number"
            value={value ?? ''}
            onChange={(e) => {
                const raw = e.target.value;
                if (raw === '') return onChange(null);
                const n = Number(raw);
                if (Number.isNaN(n)) return;
                onChange(n);
            }}
            placeholder={placeholder}
            min={min}
            max={max}
            step={step}
            className={`${inputBase} ${error ? inputError : inputDefault}`}
        />
    );

    if (!suffix) return inner;
    return (
        <div className="flex items-center gap-2">
            <div className="flex-1">{inner}</div>
            <span className="text-xs text-pt-text-dim whitespace-nowrap">{suffix}</span>
        </div>
    );
}

// ----------------------------------------------------------------------------
// DateInput
// ----------------------------------------------------------------------------

export function DateInput({
    value,
    onChange,
    error
}: {
    value: string | null;
    onChange: (v: string | null) => void;
    error?: boolean;
}) {
    // value comes as ISO date string ("2024-03-15"); native date input accepts that
    return (
        <input
            type="date"
            value={value ?? ''}
            onChange={(e) =>
                onChange(e.target.value === '' ? null : e.target.value)
            }
            className={`${inputBase} ${error ? inputError : inputDefault}`}
        />
    );
}

// ----------------------------------------------------------------------------
// SelectInput
// ----------------------------------------------------------------------------

export interface SelectOption {
    value: string;
    label: string;
}

export function SelectInput({
    value,
    onChange,
    options,
    placeholder,
    error
}: {
    value: string | null;
    onChange: (v: string | null) => void;
    options: SelectOption[];
    placeholder?: string;
    error?: boolean;
}) {
    return (
        <select
            value={value ?? ''}
            onChange={(e) =>
                onChange(e.target.value === '' ? null : e.target.value)
            }
            className={`${inputBase} ${error ? inputError : inputDefault}`}
        >
            {placeholder && <option value="">{placeholder}</option>}
            {options.map((opt) => (
                <option key={opt.value} value={opt.value}>
                    {opt.label}
                </option>
            ))}
        </select>
    );
}

// ----------------------------------------------------------------------------
// CheckboxInput — boolean toggle with label as click target
// ----------------------------------------------------------------------------

export function CheckboxInput({
    value,
    onChange,
    label
}: {
    value: boolean | null;
    onChange: (v: boolean) => void;
    label: string;
}) {
    return (
        <label className="inline-flex items-center gap-2 cursor-pointer text-sm text-pt-text">
            <input
                type="checkbox"
                checked={value === true}
                onChange={(e) => onChange(e.target.checked)}
                className="w-4 h-4 accent-pt-gold cursor-pointer"
            />
            <span>{label}</span>
        </label>
    );
}
